"use client"

import type { Variants } from "motion/react"
import { motion, useAnimation } from "motion/react"
import type { HTMLAttributes } from "react"
import * as React from "react"
import { forwardRef, useCallback, useImperativeHandle } from "react"

import { cn } from "@/lib/utils"
import { useHoverResetGuard } from "./useHoverResetGuard"

export interface CreditCardIconHandle {
  startAnimation: () => void
  stopAnimation: () => void
}

interface CreditCardIconProps extends HTMLAttributes<HTMLDivElement> {
  size?: number
  /** e.g. `inherit` from sidebar nav so stroke follows the row color */
  color?: string
}

const CARD_VARIANTS: Variants = {
  normal: {
    x: 0,
    transition: {
      type: "spring",
      stiffness: 280,
      damping: 18,
    },
  },
  animate: {
    x: [0, -4, 1.5, 0],
    transition: {
      duration: 0.7,
      times: [0, 0.4, 0.75, 1],
      ease: "easeInOut",
    },
  },
}

const CreditCardIcon = forwardRef<CreditCardIconHandle, CreditCardIconProps>(
  (
    { onMouseEnter, onMouseLeave, className, size = 28, color, style, ...props },
    ref
  ) => {
    const controls = useAnimation()
    const { withLockedHoverReset, releaseHoverResetLock, shouldApplyHoverReset } =
      useHoverResetGuard()

    const runCardAnimation = useCallback(async () => {
      await controls.start("normal", { duration: 0 })
      await controls.start("animate")
    }, [controls])

    useImperativeHandle(
      ref,
      () => ({
        startAnimation: () => void withLockedHoverReset(runCardAnimation),
        stopAnimation: () => {
          releaseHoverResetLock()
          void controls.start("normal")
        },
      }),
      [controls, releaseHoverResetLock, runCardAnimation, withLockedHoverReset]
    )

    const handleMouseEnter = useCallback(
      (e: React.MouseEvent<HTMLDivElement>) => {
        onMouseEnter?.(e)
        void runCardAnimation()
      },
      [onMouseEnter, runCardAnimation]
    )

    const handleMouseLeave = useCallback(
      (e: React.MouseEvent<HTMLDivElement>) => {
        onMouseLeave?.(e)
        if (!shouldApplyHoverReset()) return
        void controls.start("normal")
      },
      [controls, onMouseLeave, shouldApplyHoverReset]
    )

    return (
      <div
        className={cn("inline-flex shrink-0 items-center justify-center", className)}
        style={{
          color: color && color !== "inherit" ? color : undefined,
          ...style,
        }}
        onMouseEnter={handleMouseEnter}
        onMouseLeave={handleMouseLeave}
        {...props}
      >
        <svg
          className="overflow-visible text-current"
          fill="none"
          height={size}
          stroke="currentColor"
          strokeLinecap="round"
          strokeLinejoin="round"
          strokeWidth="2"
          viewBox="0 0 24 24"
          width={size}
          xmlns="http://www.w3.org/2000/svg"
        >
          <motion.g animate={controls} initial="normal" variants={CARD_VARIANTS}>
            <rect height="14" rx="2" width="20" x="2" y="5" />
            <line x1="2" x2="22" y1="10" y2="10" />
          </motion.g>
        </svg>
      </div>
    )
  }
)

CreditCardIcon.displayName = "CreditCardIcon"

export { CreditCardIcon }
